import React, { createContext, useContext, useEffect, useState } from 'react'
import { useModalActions } from '../../../../../../shared/component/modal-dialog'
import { useViewerLaunch } from '../../../../../../shared/hooks/use-viewer-launch'
import { useCanOpenInBrowser } from '../../../../../../shared/hooks/use-viewer-available'
import { useCourseWebBuilds } from '../../../../../../shared/hooks/use-course-web-builds'
import { ViewerLaunchFailure } from '../../../../../../shared/viewer/viewer-launch-failure'
import { CourseDeepLink } from '../model/course-deep-link'

// The two course actions that are not pages: "Deep link" (a dialog) and
// "Launch" (the web viewer, in a new tab). The menus that offer them -- the
// course page's header and every course card in the grid -- are rebuilt on
// every render of the page around them, so they can't hold a dialog or a
// launch in flight themselves: this host, mounted once around the whole
// content area (node-content-grid.js), holds both, and the menus ask it.
//
// One Deep link dialog serves every course; it is remounted per course so
// its link, its copy state and any launch failure are that course's.
// "Launch" is one useViewerLaunch for the area: one launch at a time, the
// tab opened inside the click (so it is called straight from the button's
// or menu item's click, before any await), and a failure shown in the
// shared launch-failure modal, since a menu has closed by then.
//
// Which courses have a web build is one read for the area too
// (use-course-web-builds.js), made only for someone who can launch: Launch
// waits for it, and a course without one offers no Launch (owner 2026-10-09:
// its page says nothing more; the card's platform icons say where it runs).

export const DEEP_LINK_TARGET = 'courseLink'

const CourseActionsContext = createContext(null)

// Outside a host (nothing in the app renders a course menu there) neither
// action is offered.
const NO_HOST = {
  present: false,
  canLaunch: false,
  launching: false,
  hasWebBuild: () => undefined,
  openDeepLink: () => {},
  launch: () => {}
}

export const useCourseActionsHost = () => useContext(CourseActionsContext) || NO_HOST

export const CourseActionsHost = ({ children }) => {
  const [deepLink, setDeepLink] = useState(null)
  const modalActions = useModalActions(DEEP_LINK_TARGET)

  // Shown once the dialog for the chosen course is in the page.
  useEffect(
    () => {
      if (deepLink) modalActions.show()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deepLink]
  )

  const canOpenInBrowser = useCanOpenInBrowser()
  const webBuilds = useCourseWebBuilds(canOpenInBrowser)
  const viewerLaunch = useViewerLaunch()

  const host = {
    present: true,
    // Shown once both answers are in: whether there is a viewer here (and
    // the caller may use it), then which courses have a web build.
    canLaunch: canOpenInBrowser && webBuilds.settled,
    launching: viewerLaunch.launching,
    // true / false, or undefined when the check failed (Launch stays enabled).
    hasWebBuild: webBuilds.has,
    // A new object each time, so choosing the same course again shows it again.
    openDeepLink: nodeID => setDeepLink({ nodeID }),
    launch: nodeID => viewerLaunch.launch(`content/${nodeID}`)
  }

  return (
    <CourseActionsContext.Provider value={host}>
      {children}
      {deepLink && <CourseDeepLink key={deepLink.nodeID} targetID={DEEP_LINK_TARGET} nodeID={deepLink.nodeID} webBuild={webBuilds.has(deepLink.nodeID)} />}
      {viewerLaunch.failure && <ViewerLaunchFailure failure={viewerLaunch.failure} onClose={viewerLaunch.dismiss} />}
    </CourseActionsContext.Provider>
  )
}
